/**
 * PURPOSE: Presentación de la asignación inteligente de camisetas en Programación (FTR-0041, FTR-0043, FTR-0044).
 * RESPONSIBILITIES: Armar el cuerpo del pedido (alcance —todos, solo con faltantes o una selección—, la fase —todas,
 *   la de grupos o una etapa final—, qué uniformes —principales, alternos o ambos—, completar faltantes o
 *   recalcular), rotular acciones, clases y
 *   estados, armar las tarjetas por partido (por lado: camiseta principal y alterno) con sus filtros, agrupar los
 *   partidos elegibles por fecha/etapa y separar las advertencias por severidad y tipo (reglas de color, mismo
 *   uniforme, mismo tono con otra camiseta, alternos del mismo color). No calcula ninguna asignación: el
 *   algoritmo vive en el backend.
 * COLLABORATORS: AssignUniformsModal.jsx, uniformOptions.utils.js (contextualOptionLabel).
 * ROUTE: winscore/src/utils/uniformAutoAssignDisplay.js
 */
import { contextualOptionLabel } from "../admin/AdminCompany/uniformOptions.utils.js";

export const AUTO_SCOPES = Object.freeze({ ALL_ELIGIBLE: "ALL_ELIGIBLE", MISSING: "MISSING", SELECTED: "SELECTED" });

export const ACTION_LABELS = Object.freeze({
  NEW: "Nuevo",
  PRESERVED: "Conservado",
  MODIFIED: "Modificado",
  UNCHANGED: "Sin cambio",
  UNASSIGNABLE: "No asignable",
});

export const ACTION_TONES = Object.freeze({
  NEW: "emerald",
  PRESERVED: "slate",
  MODIFIED: "amber",
  UNCHANGED: "slate",
  UNASSIGNABLE: "rose",
});

const EXCLUSIVE_LABELS = Object.freeze({ GOALKEEPER: "Arquero", LIBERO: "Líbero" });
const EXCLUSIVE_PLURALS = Object.freeze({ GOALKEEPER: "Arqueros", LIBERO: "Líberos" });

export const kindText = (kind, exclusiveStrategy) =>
  kind === "MAIN" ? "Camiseta" : EXCLUSIVE_LABELS[exclusiveStrategy] || "Exclusivo";

/** "Arqueros" / "Líberos" (alternos del deporte). */
export const alternatePlural = (exclusiveStrategy) => EXCLUSIVE_PLURALS[exclusiveStrategy] || "Alternos";

/** Clases de uniforme trabajadas: por defecto ambas (FTR-0044). */
export const BOTH_KINDS = Object.freeze({ main: true, alternate: true });

const LEGACY_STATUS_TEXT = Object.freeze({
  LEGACY_TEXT: "texto",
  STALE: "obsoleto",
});

/** Texto de lo que hay hoy en un lado: opción, texto legacy, enlace obsoleto o nada. */
export const currentText = (current) => {
  if (!current) return "—";
  if (current.status === "OPTION" && current.option) return contextualOptionLabel(current.option);
  if (current.status === "LEGACY_TEXT") return current.text ? `“${current.text}” (${LEGACY_STATUS_TEXT.LEGACY_TEXT})` : "—";
  if (current.status === "STALE") return `${current.text ? `“${current.text}”` : "Sin texto"} (${LEGACY_STATUS_TEXT.STALE})`;
  return "—";
};

export const proposedText = (entry) => {
  if (entry.action === "PRESERVED") return currentText(entry.current);
  if (!entry.proposed) return "—";
  return contextualOptionLabel(entry.proposed);
};

// ------------------------------------------------------------------------------------------
// Fase (asignación por fase): "" = todas; "1:" = fase de grupos; "2:3" = la etapa final 3.
// ------------------------------------------------------------------------------------------
export const ALL_PHASES_KEY = "";

export const phaseKeyOf = (phase) => (phase ? `${phase.idtypephase}:${phase.nroFecha ?? ""}` : ALL_PHASES_KEY);

export const phaseFromKey = (key) => {
  if (!key) return null;
  const [type, fecha] = String(key).split(":");
  const idtypephase = Number(type);
  if (![1, 2].includes(idtypephase)) return null;
  const nroFecha = fecha ? Number(fecha) : null;
  return { idtypephase, nroFecha: Number.isInteger(nroFecha) && nroFecha > 0 ? nroFecha : null };
};

/** ¿El partido (elegible o de la vista previa) es de la fase? Sin fase, todos. */
export const isInPhase = (match, phase) =>
  !phase ||
  (Number(match.idtypephase) === phase.idtypephase && (phase.nroFecha === null || Number(match.nroFecha) === phase.nroFecha));

/** Opciones del selector: todas las fases y las que devolvió la vista previa, con cuántos partidos se pueden asignar. */
export const phaseSelectOptions = (phases = [], currentKey = ALL_PHASES_KEY) => {
  const options = [
    { key: ALL_PHASES_KEY, label: "Todas las fases" },
    ...phases.map((phase) => ({
      key: phaseKeyOf(phase),
      label: `${phase.label} (${phase.eligible} asignable${phase.eligible === 1 ? "" : "s"})`,
    })),
  ];
  if (!options.some((option) => option.key === currentKey)) options.push({ key: currentKey, label: "Fase elegida" });
  return options;
};

/**
 * Qué se puede asignar en la fase elegida: la regla depende del tipo de fase (FTR-0065: en fases finales el
 * algoritmo solo asigna partidos no iniciados, publicados o no; los demás se corrigen a mano, partido por partido).
 */
export const phaseHint = (phase, summary = null) => {
  if (!phase) {
    return "Grupos: solo partidos por jugarse y no publicados. Fases finales: solo los no iniciados, publicados o no; los en vivo, finalizados y oficializados se omiten.";
  }
  const rule =
    phase.idtypephase === 1
      ? "Solo partidos por jugarse y no publicados."
      : "Solo partidos no iniciados, publicados o no. Los en vivo, finalizados y oficializados se omiten y se corrigen a mano.";
  if (!summary) return rule;
  const parts = [`${summary.eligible} asignable${summary.eligible === 1 ? "" : "s"}`];
  if (summary.pending) parts.push(`${summary.pending} por definir`);
  if (summary.blocked) {
    parts.push(`${summary.blocked} ${phase.idtypephase === 1 ? "publicado(s) o ya jugado(s)" : "ya iniciado(s), oficializado(s) o no habilitado(s)"} que no se tocan`);
  }
  return `${rule} ${parts.join(" · ")}.`;
};

/**
 * Cuerpo del pedido de vista previa / aplicación. `uniforms` solo se envía cuando se restringe la clase (por
 * defecto el backend trabaja principales y alternos) y `phase` solo con una fase elegida (nunca con una selección
 * manual: los partidos ya están elegidos), así el pedido por defecto no cambia.
 */
export const buildAutoAssignBody = ({ idevent, idsport, scopeMode, selectedIds = [], recalculate = false, kinds = BOTH_KINDS, phase = null }) => {
  let scope;
  if (scopeMode === AUTO_SCOPES.SELECTED) {
    scope = { mode: AUTO_SCOPES.SELECTED, idmatches: [...new Set(selectedIds.map(Number))].sort((a, b) => a - b) };
  } else if (scopeMode === AUTO_SCOPES.MISSING) {
    scope = { mode: AUTO_SCOPES.MISSING };
  } else {
    scope = { mode: AUTO_SCOPES.ALL_ELIGIBLE };
  }
  const body = { idevent: Number(idevent), idsport: Number(idsport), scope, recalculate: Boolean(recalculate) };
  if (kinds && (kinds.main === false || kinds.alternate === false)) {
    body.uniforms = { main: kinds.main !== false, alternate: kinds.alternate !== false };
  }
  if (phase && scope.mode !== AUTO_SCOPES.SELECTED) {
    body.phase = { idtypephase: phase.idtypephase, ...(phase.nroFecha ? { nroFecha: phase.nroFecha } : {}) };
  }
  return body;
};

/** Ayuda del modo elegido según las clases de uniforme trabajadas. */
export const modeHint = ({ recalculate, kinds = BOTH_KINDS, exclusiveStrategy }) => {
  const alternates = alternatePlural(exclusiveStrategy).toLowerCase();
  const onlyMain = kinds.main && kinds.alternate === false && Boolean(exclusiveStrategy);
  const onlyAlternate = kinds.main === false && kinds.alternate;
  if (!recalculate) {
    if (onlyMain) return `Completa solo camisetas principales vacías; lo ya asignado se conserva y los ${alternates} no se tocan.`;
    if (onlyAlternate) return `Completa solo ${alternates} vacíos; lo ya asignado, incluidas las camisetas principales, se conserva.`;
    return "Conserva todo lo ya asignado; solo completa lo vacío y lo que ahora se puede resolver.";
  }
  if (onlyMain) return `Vuelve a proponer las camisetas principales; los ${alternates} no se tocan. Revisa el antes → después.`;
  if (onlyAlternate) return `Vuelve a proponer solo los ${alternates}; las camisetas principales no se tocan. Revisa el antes → después.`;
  return "También vuelve a proponer lo ya asignado; revisa el antes → después.";
};

/** Entradas agrupadas por partido, en el orden del backend (cronológico). */
export const groupEntriesByMatch = (entries = []) => {
  const groups = new Map();
  for (const entry of entries) {
    if (!groups.has(entry.idmatch)) groups.set(entry.idmatch, { idmatch: entry.idmatch, label: entry.label, stage: entry.stage, rows: [] });
    groups.get(entry.idmatch).rows.push(entry);
  }
  return [...groups.values()];
};

const CHANGE_ACTIONS = new Set(["NEW", "MODIFIED"]);

/**
 * Tarjetas por partido (FTR-0044): por lado, la institución, su camiseta principal (MAIN) y su alterno
 * (EXCLUSIVE), más las advertencias del partido. Marca si hay cambios, advertencias o lados sin asignar.
 */
export const buildMatchCards = (entries = [], warnings = []) => {
  const cards = new Map();
  for (const entry of entries) {
    if (!cards.has(entry.idmatch)) {
      cards.set(entry.idmatch, { idmatch: entry.idmatch, label: entry.label, stage: entry.stage, sides: new Map(), warnings: [] });
    }
    const card = cards.get(entry.idmatch);
    if (!card.sides.has(entry.side)) card.sides.set(entry.side, { side: entry.side, institution: entry.institution || null, MAIN: null, EXCLUSIVE: null });
    card.sides.get(entry.side)[entry.kind === "MAIN" ? "MAIN" : "EXCLUSIVE"] = entry;
  }
  for (const warning of warnings) {
    if (warning.idmatch !== null && warning.idmatch !== undefined && cards.has(warning.idmatch)) cards.get(warning.idmatch).warnings.push(warning);
  }
  return [...cards.values()].map((card) => {
    const sides = [...card.sides.values()].sort((left, right) => left.side - right.side);
    const rows = sides.flatMap((side) => [side.MAIN, side.EXCLUSIVE].filter(Boolean));
    return {
      ...card,
      sides,
      changed: rows.some((row) => CHANGE_ACTIONS.has(row.action)),
      unassigned: rows.some((row) => row.action === "UNASSIGNABLE"),
      warned: card.warnings.length > 0,
      strong: card.warnings.some((warning) => warning.severity === "STRONG"),
    };
  });
};

export const CARD_FILTERS = Object.freeze({ ALL: "ALL", CHANGES: "CHANGES", WARNINGS: "WARNINGS", UNASSIGNED: "UNASSIGNED" });
export const CARD_FILTER_LABELS = Object.freeze({
  ALL: "Todos",
  CHANGES: "Con cambios",
  WARNINGS: "Con advertencias",
  UNASSIGNED: "Sin asignar",
});

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

/** Filtro de tarjetas por estado y texto (partido o institución, sin distinguir tildes ni mayúsculas). */
export const filterMatchCards = (cards = [], { filter = CARD_FILTERS.ALL, search = "" } = {}) => {
  const term = normalize(search);
  return cards.filter((card) => {
    if (filter === CARD_FILTERS.CHANGES && !card.changed) return false;
    if (filter === CARD_FILTERS.WARNINGS && !card.warned) return false;
    if (filter === CARD_FILTERS.UNASSIGNED && !card.unassigned) return false;
    if (!term) return true;
    const haystack = [card.label, card.stage, ...card.sides.map((side) => side.institution?.name)].map(normalize).join(" ");
    return haystack.includes(term);
  });
};

/** Partidos elegibles agrupados por fecha/etapa para la selección manual. */
export const groupEligibleMatches = (eligibleMatches = []) => {
  const groups = new Map();
  for (const match of eligibleMatches) {
    const key = `${match.idtypephase}:${match.nroFecha ?? "none"}`;
    if (!groups.has(key)) groups.set(key, { key, stage: match.stage, matches: [] });
    groups.get(key).matches.push(match);
  }
  return [...groups.values()];
};

/**
 * Excepciones a la alternancia de camisetas, siempre visibles y nombradas en la confirmación (FTR-0042/0043):
 * - repetir el MISMO uniforme concreto (tono + colores) en partidos seguidos, evitable solo a un costo mayor o
 *   inevitable porque la institución tiene una sola camiseta;
 * - repetir el tono con OTRA camiseta (menos grave). Los códigos anteriores al FTR-0043 se agrupan aquí para
 *   convivir con un backend sin actualizar.
 * La repetición crítica (3.ª vez seguida) y el mismo tono en ambos equipos son advertencias fuertes.
 * FTR-0044: las reglas de color (mismo color que el rival, principal = alterno, alterno sin opción válida,
 * alterno = principal rival, conflicto en lo ya asignado) son fuertes; dos alternos del mismo color es el último
 * recurso y se muestra aparte.
 */
export const UNIFORM_REPEAT_CODES = Object.freeze(["REPETICION_UNIFORME", "REPETICION_UNIFORME_INEVITABLE"]);
export const TONE_REPEAT_CODES = Object.freeze(["REPETICION_TONO", "SIN_TONO_PARA_ALTERNAR", "PREFERENCIA_NO_RESPETADA", "ALTERNANCIA_INTERRUMPIDA"]);
export const TONE_CONFLICT_CODES = Object.freeze([...UNIFORM_REPEAT_CODES, ...TONE_REPEAT_CODES]);
export const SAME_TONE_CODE = "MISMO_TONO_OBLIGADO";
export const CRITICAL_REPEAT_CODE = "REPETICION_UNIFORME_CRITICA";
export const COLOR_RULE_CODES = Object.freeze([
  "MISMO_COLOR_PRINCIPAL",
  "PRINCIPAL_IGUAL_ALTERNO",
  "ALTERNO_SIN_OPCION_VALIDA",
  "ALTERNO_IGUAL_PRINCIPAL_RIVAL",
  "CONFLICTO_EXISTENTE",
]);
export const ALTERNATE_PAIR_CODE = "ALTERNOS_MISMO_COLOR";

export const splitWarnings = (warnings = []) => {
  const warning = warnings.filter((item) => item.severity === "WARNING");
  const uniformRepeats = warning.filter((item) => UNIFORM_REPEAT_CODES.includes(item.code));
  const toneRepeats = warning.filter((item) => TONE_REPEAT_CODES.includes(item.code));
  const alternatePairs = warning.filter((item) => item.code === ALTERNATE_PAIR_CODE);
  return {
    strong: warnings.filter((item) => item.severity === "STRONG"),
    warning,
    conflicts: [...uniformRepeats, ...toneRepeats],
    uniformRepeats,
    toneRepeats,
    colorRules: warnings.filter((item) => COLOR_RULE_CODES.includes(item.code)),
    alternatePairs,
    otherWarnings: warning.filter((item) => !TONE_CONFLICT_CODES.includes(item.code) && item.code !== ALTERNATE_PAIR_CODE),
    info: warnings.filter((item) => item.severity === "INFO"),
  };
};

const joinParts = (parts) => (parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} y ${parts[parts.length - 1]}`);

/**
 * Texto de la confirmación antes de aplicar. Con el detalle (FTR-0043/0044) nombra cada categoría; sin él
 * mantiene los textos anteriores (strongCount = mismo tono, conflictCount = excepciones).
 */
export const confirmationText = ({
  strongCount = 0,
  conflictCount = 0,
  writes = 0,
  sameToneCount,
  criticalCount,
  colorRuleCount = 0,
  uniformRepeatCount,
  toneRepeatCount,
  alternatePairCount = 0,
} = {}) => {
  const parts = [];
  if (sameToneCount === undefined && criticalCount === undefined) {
    if (strongCount) parts.push(`${strongCount} partido(s) con el mismo tono en ambos equipos`);
  } else {
    if (sameToneCount) parts.push(`${sameToneCount} partido(s) con el mismo tono en ambos equipos`);
    if (criticalCount) parts.push(`${criticalCount} repetición(es) crítica(s) del mismo uniforme`);
    if (colorRuleCount) parts.push(`${colorRuleCount} conflicto(s) de color entre camisetas y alternos`);
    const otherStrong = strongCount - (sameToneCount || 0) - (criticalCount || 0) - (colorRuleCount || 0);
    if (otherStrong > 0) parts.push(`${otherStrong} advertencia(s) fuerte(s)`);
  }
  if (uniformRepeatCount === undefined && toneRepeatCount === undefined) {
    if (conflictCount) parts.push(`${conflictCount} excepción(es) a la alternancia de camisetas`);
  } else {
    if (uniformRepeatCount) parts.push(`${uniformRepeatCount} repetición(es) del mismo uniforme`);
    if (toneRepeatCount) parts.push(`${toneRepeatCount} repetición(es) de tono con otra camiseta`);
  }
  if (alternatePairCount) parts.push(`${alternatePairCount} partido(s) con alternos del mismo color`);
  if (!parts.length) return `Se guardarán ${writes} asignación(es). ¿Continuar?`;
  return `La propuesta incluye ${joinParts(parts)}. ¿Continuar y guardar ${writes} asignación(es)?`;
};

/** Contadores visibles del resumen (los ceros se muestran igual: el usuario ve qué no ocurre). */
export const summaryItems = (summary = {}) => [
  { key: "new", label: "Nuevos", value: summary.new ?? 0 },
  { key: "preserved", label: "Conservados", value: summary.preserved ?? 0 },
  { key: "modified", label: "Modificados", value: summary.modified ?? 0 },
  { key: "unchanged", label: "Sin cambio", value: summary.unchanged ?? 0 },
  { key: "unassignable", label: "No asignables", value: summary.unassignable ?? 0 },
  { key: "pending", label: "Pendientes", value: summary.pending ?? 0 },
  { key: "warnings", label: "Advertencias", value: (summary.warnings ?? 0) + (summary.strongWarnings ?? 0) },
];

/**
 * Resumen por clase (FTR-0044): una fila para camisetas principales y otra para el alterno del deporte, solo con
 * las clases trabajadas. Sin `byKind` (backend anterior) devuelve una lista vacía.
 */
export const kindSummaryRows = (summary = {}, { uniforms = BOTH_KINDS, exclusiveStrategy } = {}) => {
  const byKind = summary.byKind;
  if (!byKind) return [];
  const rows = [];
  const item = (kindSummary = {}) => [
    { key: "new", label: "Nuevos", value: kindSummary.new ?? 0 },
    { key: "preserved", label: "Conservados", value: kindSummary.preserved ?? 0 },
    { key: "modified", label: "Modificados", value: kindSummary.modified ?? 0 },
    { key: "unchanged", label: "Sin cambio", value: kindSummary.unchanged ?? 0 },
    { key: "unassignable", label: "No asignables", value: kindSummary.unassignable ?? 0 },
  ];
  if (uniforms.main !== false) rows.push({ kind: "MAIN", title: "Camisetas principales", items: item(byKind.MAIN) });
  if (exclusiveStrategy && uniforms.alternate !== false) rows.push({ kind: "EXCLUSIVE", title: alternatePlural(exclusiveStrategy), items: item(byKind.EXCLUSIVE) });
  return rows;
};

export const NOT_ELIGIBLE_TEXT = Object.freeze({
  NOT_FOUND: "no pertenece a este deporte",
  BYE: "es un descanso (BYE)",
  PUBLISHED: "está publicado",
  STATE: "ya no está por jugarse",
  // Fases finales (FTR-0065): el algoritmo solo asigna partidos no iniciados.
  LIVE: "En vivo",
  FINISHED: "Finalizado",
  OFFICIALIZED: "Oficializado",
  DISABLED: "No habilitado (juego bajo protesta)",
});

/** Motivos de fase final: esos partidos se corrigen a mano, partido por partido. */
export const MANUAL_ONLY_REASONS = Object.freeze(["LIVE", "FINISHED", "OFFICIALIZED", "DISABLED"]);
